import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { chatMessages } from "@/mocks/streams";

interface LocalMessage {
  id: string;
  user: string;
  text: string;
  accent: boolean;
  self?: boolean;
}

interface ChatPanelProps {
  channelId: string;
}

export default function ChatPanel({ channelId }: ChatPanelProps) {
  const [messages, setMessages] = useState<LocalMessage[]>(chatMessages);
  const [input, setInput] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMessages(chatMessages);
    setInput("");
  }, [channelId]);

  useEffect(() => {
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const send = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = input.trim();
    if (!text) return;
    setMessages((prev) => [
      ...prev,
      { id: `self-${Date.now()}`, user: "我", text, accent: true, self: true },
    ]);
    setInput("");
  };

  return (
    <div className="rounded-xl border border-background-200/70 bg-background-100">
      <div className="flex items-center justify-between border-b border-background-200/70 px-4 py-3">
        <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
          <span className="w-5 h-5 flex items-center justify-center text-accent-400">
            <i className="ri-chat-3-line"></i>
          </span>
          实时互动区
        </h2>
        <span className="rounded-md bg-accent-500/15 px-2 py-0.5 text-[10px] text-accent-400">演示</span>
      </div>

      <div ref={listRef} className="h-72 space-y-2 overflow-y-auto px-4 py-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start gap-2 rounded-md px-3 py-2 ${
              message.self ? "bg-accent-500/10" : "bg-background-50"
            }`}
          >
            <span className="mt-0.5 w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100/10 text-[10px] text-foreground-400">
              {message.user.slice(0, 1)}
            </span>
            <p className="text-xs leading-relaxed text-foreground-600">
              <span className={message.accent ? "text-accent-400" : "text-foreground-400"}>{message.user}：</span>
              {message.text}
            </p>
          </div>
        ))}
      </div>

      <form onSubmit={send} className="sticky bottom-0 flex items-center gap-2 border-t border-background-200/70 bg-background-100 px-4 py-3">
        <div className="flex flex-1 items-center rounded-full border border-background-200 bg-background-50 px-3 py-2 focus-within:border-accent-400">
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            maxLength={60}
            placeholder="说点什么…（演示，仅本地显示）"
            className="w-full bg-transparent px-2 text-sm text-foreground-800 placeholder-foreground-300 outline-none"
          />
        </div>
        <button
          type="submit"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-background-50 transition-colors hover:bg-accent-600"
          aria-label="发送"
        >
          <i className="ri-send-plane-fill"></i>
        </button>
      </form>

      <p className="px-4 pb-4 text-xs leading-relaxed text-foreground-400">
        弹幕与聊天为界面演示，发送的消息仅在当前页面本地显示，不会真正发送给主播或其他观众。
      </p>
    </div>
  );
}